import React, { useEffect, useState } from 'react';
import axios from 'axios';

const ViewProfile = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Handle screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch admin profile
useEffect(() => {
  const fetchProfile = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("usertype") || "admin";
      const sessionId = localStorage.getItem("currentSessionId");
      const teacherUserId = localStorage.getItem("TeacheruserId");

      if (!userId || !token) {
        setError("Invalid session");
        return;
      }

      let url = "";
      let config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      if (userType === "teacher") {
        url = `${process.env.REACT_APP_API_BASE_URL}/api/teachers/getTeacherById/${teacherUserId}`;

        if (!sessionId) {
          setError("Session not selected");
          return;
        }

        config.params = { sessionId };
      } else {
        url = `${process.env.REACT_APP_API_BASE_URL}/api/auth/admin/${userId}`;
      }

      const response = await axios.get(url, config);
      setAdmin(response.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  fetchProfile();
}, []);

  // Professional date formatter
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="text-center mt-10">Loading profile...</div>;
  }

  if (error) {
    return <div className="text-center mt-10 text-red-500">{error}</div>;
  }

  return (
    <div className="bg-white min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8">
          <h1 className="text-3xl font-semibold">
  {localStorage.getItem("userType") === "teacher"
    ? "Teacher Profile"
    : "Admin Profile"}
</h1>

        </header>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {/* Profile Image */}
          <div className="flex justify-center row-span-3">
            <img
              src={admin.image || 'https://via.placeholder.com/256'}
              alt="Admin"
              className="rounded-lg shadow-lg w-64 h-64 object-cover"
            />
          </div>

          {/* Basic Info */}
          {admin.username && (
            <ProfileCard icon="fa-id-badge" label="Username">
              @{admin.username}
            </ProfileCard>
          )}

          {admin.usertype && (
            <ProfileCard icon="fa-user-cog" label="Account Type">
              {admin.usertype.charAt(0).toUpperCase() + admin.usertype.slice(1)}
            </ProfileCard>
          )}

          {admin.role && (
            <ProfileCard icon="fa-user-tie" label="Role">
              {admin.role}
            </ProfileCard>
          )}

          {/* Contact */}
          {admin.email && (
            <ProfileCard icon="fa-envelope" label="Email">
              {admin.email}
            </ProfileCard>
          )}

          {admin.phoneNo && (
            <ProfileCard icon="fa-phone" label="Phone Number">
              {admin.phoneNo}
            </ProfileCard>
          )}

          {admin.phoneNo2 && (
            <ProfileCard icon="fa-phone-alt" label="Alternate Phone">
              {admin.phoneNo2}
            </ProfileCard>
          )}

          {/* Location */}
          {admin.city && (
            <ProfileCard icon="fa-city" label="City">
              {admin.city}
            </ProfileCard>
          )}

          {admin.state && (
            <ProfileCard icon="fa-landmark" label="State">
              {admin.state}
            </ProfileCard>
          )}

          {admin.address && (
            <ProfileCard icon="fa-map-marker-alt" label="Address">
              {admin.address}
            </ProfileCard>
          )}

          {/* Account Meta (Professional) */}
          {admin.createdAt && (
            <ProfileCard icon="fa-calendar-plus" label="Account Created On">
              {formatDate(admin.createdAt)}
            </ProfileCard>
          )}

          {admin.updatedAt && (
            <ProfileCard icon="fa-calendar-alt" label="Last Profile Update">
              {formatDate(admin.updatedAt)}
            </ProfileCard>
          )}
        </div>
      </div>
    </div>
  );
};

// Reusable professional card
const ProfileCard = ({ icon, label, children }) => (
  <div className="bg-gray-100 p-4 rounded-lg shadow-md">
    <strong className="block text-gray-700 text-[16px] mb-1">
      <i className={`fas ${icon} mr-2 text-blue-500`}></i>
      {label}
    </strong>
    <div className="text-gray-900 text-[13px]">{children}</div>
  </div>
);

export default ViewProfile;